import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Plus, LayoutGrid, List, Search } from "lucide-react";
import ExportDialog from "@/components/exports/ExportDialog";
import { useCanExport } from "@/hooks/useCanExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useGetDealStagesQuery, useGetDealsQuery, useGetPipelineSummaryQuery } from "@/store/rest_api";
import DealStage from "@/model/interface/DealStage";
import { DealStageCategory, stageCategoryOf } from "@/enum/DealStageCategory";
import { DealKanbanBoard } from "@/components/pages/deals/DealKanbanBoard";
import { DealListView } from "@/components/pages/deals/DealListView";
import { DealFormDialog } from "@/components/pages/deals/DealFormDialog";
import { PipelineSummaryCards } from "@/components/pages/deals/PipelineSummaryCards";

export default function DealsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "kanban";
  const showLeads = searchParams.get("leads") === "shown";
  const [search, setSearch] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { canExport } = useCanExport();

  const { data: stages = [] as DealStage[], isLoading: stagesLoading } = useGetDealStagesQuery({});
  const { data: summary } = useGetPipelineSummaryQuery();
  const { data: dealsData, isLoading: dealsLoading } = useGetDealsQuery({
    size: 200,
    q: search || undefined,
    includeLeads: showLeads,
  });

  const deals = dealsData?.items || [];
  const visibleStages = showLeads
    ? stages
    : stages.filter((stage) => stageCategoryOf(stage) !== DealStageCategory.LEAD);

  const updateSearchParam = (key: string, value: string | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
    setSearchParams(nextParams);
  };

  const setView = (nextView: string) => {
    updateSearchParam("view", nextView);
  };

  const setShowLeads = (shown: boolean) => {
    updateSearchParam("leads", shown ? "shown" : null);
  };

  return (
    <div className="flex flex-col h-full w-0 min-w-full overflow-hidden">
      <div className="flex items-center justify-between p-4 pb-2 shrink-0">
        <h1 className="text-3xl font-bold tracking-tight">Deal Pipeline</h1>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-2">
            <Switch checked={showLeads} onCheckedChange={setShowLeads} />
            Show leads
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search deals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64"
            />
          </div>
          <div className="flex border rounded-md">
            <Button
              variant={view === "kanban" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("kanban")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={view === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          {canExport && (
            <Button variant="outline" onClick={() => setExportOpen(true)}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          )}
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Deal
          </Button>
        </div>
      </div>

      <div className="shrink-0">
        <PipelineSummaryCards stages={visibleStages} summary={summary} showLeads={showLeads} />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden px-4 pb-4">
        {view === "kanban" ? (
          <DealKanbanBoard
            stages={visibleStages}
            deals={deals}
            isLoading={stagesLoading || dealsLoading}
          />
        ) : (
          <DealListView
            deals={deals}
            stages={visibleStages}
            isLoading={dealsLoading}
          />
        )}
      </div>

      <DealFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={search ? "Export matching deals" : "Export deals"}
        description="Stage, money fields, closing date, assignee, lead and property for every deal that matches your search."
        request={{ kind: "DEALS", dealFilter: { q: search || undefined } }}
      />
    </div>
  );
}
